import type { Collectable } from "../lib/api";
import { formatCents } from "../lib/money";
import { jarHue } from "./Jar";

/* "Your allowance is here. Come and get it."
 *
 * This is the happy sibling of the pendingReview card and sits in the same
 * place, because to a child they are answers to the same question: where is
 * my money. One says a grown-up is still deciding; this one says it is done
 * and it is yours.
 *
 * The money is ALREADY in the jars -- the payout posted at payday and the
 * ledger has been carrying it ever since. What waits is the telling. The jars
 * on this screen hold the figure back until the child taps, so that the
 * allowance arrives by being handed over rather than by having appeared while
 * nobody was looking, which is the whole of what this card is for.
 */
export function PaydayCard({
  collectable,
  onCollect,
  busy,
}: {
  collectable: Collectable;
  onCollect: () => void;
  busy: boolean;
}) {
  return (
    <div
      style={{
        margin: "0 0 16px",
        padding: "16px 16px 14px",
        background: "var(--save-soft)",
        border: "2px solid var(--save)",
        borderRadius: "var(--r-lg)",
        textAlign: "center",
      }}
    >
      <p
        style={{
          margin: 0,
          fontFamily: "var(--font-display)",
          fontSize: 17,
          fontWeight: 600,
          color: "var(--save-deep)",
        }}
      >
        Your allowance for {collectable.periodLabel} is here!
      </p>

      <p
        className="money"
        style={{
          margin: "6px 0 2px",
          fontSize: 40,
          fontWeight: 700,
          lineHeight: 1.1,
          color: "var(--save-deep)",
        }}
      >
        {formatCents(collectable.amountCents)}
      </p>

      {/* What is about to happen, before it happens. A child who can see the
          shares coming knows the jars are not taking anything from them. One
          share per item rather than a single string joined with separators:
          the gaps do the dividing, and a share that wraps takes its own
          amount with it instead of stranding a dot at the end of a line. */}
      <p
        style={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "center",
          gap: "2px 14px",
          margin: "0 0 12px",
          fontSize: 13.5,
          color: "var(--ink-2)",
        }}
      >
        {collectable.parts
          .filter((p) => p.amountCents > 0)
          .map((p) => (
            <span key={p.key}>
              <span className="money">{formatCents(p.amountCents)}</span>{" "}
              {p.name.toLowerCase()}
            </span>
          ))}
      </p>

      <button
        type="button"
        onClick={onCollect}
        disabled={busy}
        style={{
          width: "100%",
          minHeight: "var(--tap-min)",
          borderRadius: "var(--r-md)",
          border: "none",
          background: "var(--save)",
          color: "#fff",
          fontFamily: "var(--font-display)",
          fontSize: 20,
          fontWeight: 700,
          cursor: busy ? "default" : "pointer",
          opacity: busy ? 0.6 : 1,
        }}
      >
        {busy ? "Counting it out…" : "Collect it"}
      </button>
    </div>
  );
}

/* The same card mid-pour: the money leaving the envelope.
 *
 * The total counts DOWN by exactly what has landed in the jars so far, so the
 * two halves of the screen always add up to the payout. Nothing is created or
 * lost on the way across, which is the point a child is meant to take from
 * watching it.
 */
export function PayingOut({
  collectable,
  pouredTotal,
  activeName,
}: {
  collectable: Collectable;
  pouredTotal: number;
  activeName: string | null;
}) {
  const left = collectable.amountCents - pouredTotal;

  return (
    <div
      style={{
        margin: "0 0 16px",
        padding: "16px",
        background: "var(--save-soft)",
        border: "2px solid var(--save)",
        borderRadius: "var(--r-lg)",
        textAlign: "center",
      }}
      aria-live="polite"
    >
      <p
        className="money"
        style={{
          margin: 0,
          fontSize: 40,
          fontWeight: 700,
          lineHeight: 1.1,
          color: left === 0 ? "var(--ink-3)" : "var(--save-deep)",
          transition: "color 300ms linear",
        }}
      >
        {formatCents(left)}
      </p>
      <p style={{ margin: "6px 0 0", fontSize: 15, color: "var(--ink-2)",
                  minHeight: 22 }}>
        {activeName ? `Into ${activeName}…` : "All in your jars!"}
      </p>
    </div>
  );
}

/** The glow a jar wears while money is going into it. Lives here rather than
 *  in Jar so the jar itself stays a drawing of a jar. */
export function receivingGlow(kind: string): string {
  return `0 0 0 3px var(--paper), 0 0 0 6px ${jarHue(kind).line}`;
}
